import { getAllowedCountrySelectOptions } from "../../utils/general";
import { sanitizeInAppNotificationHtml } from "../../utils/inAppNotificationUtils";
import {
  notificationValidations,
  PUSH_MAX_CTAS,
  PUSH_SELECT_ALL_COUNTRIES,
  PUSH_TYPE_CATEGORY_MAP,
} from "./notificationConstant";

export function csvToArray(str, delimiter = ",") {
  const headers = str.slice(0, str.indexOf("\n")).split(delimiter);
  const rows = str.slice(str.indexOf("\n") + 1).split("\n");
  if (rows && rows.length > 0) {
    if (rows[rows.length - 1] === "") {
      rows.splice(-1);
    }
  }
  if (!rows || rows.length === 0) {
    return [];
  }
  return rows.map(function (row) {
    const values = row.split(delimiter);
    const el = headers.reduce(function (object, header, index) {
      object[header] = values[index];
      return object;
    }, {});
    return el;
  });
}

export function parseCsvUserIdsFromFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve([]);
      return;
    }
    const reader = new FileReader();
    reader.onload = function (event) {
      const textData = event.target.result;
      let arrFileData = csvToArray(textData);
      if (arrFileData.length === 0) {
        resolve([]);
        return;
      }
      arrFileData = arrFileData.map((element) =>
        Object.values(element)[0].replace(/(\r\n|\n|\r)/gm, "")
      );
      resolve(arrFileData);
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read CSV file"));
    reader.readAsText(file);
  });
}

export function stripHtmlToPlainText(html = "") {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export function toNotificationExpiryEndOfDay(date) {
  if (!date) return null;
  const expiry = new Date(date);
  expiry.setHours(23, 59, 59, 0);
  return expiry.getTime();
}

export function isNotificationExpiryInFuture(expiryDateMs) {
  return Boolean(expiryDateMs && expiryDateMs > Date.now());
}

export function getNotificationExpiryUnixSeconds(expiryDateMs) {
  if (!expiryDateMs) return null;
  return Math.floor(expiryDateMs / 1000);
}

export function getPushCountryCodes(allowedCountries = []) {
  return getAllowedCountrySelectOptions(allowedCountries, {
    includeNone: false,
  }).map((option) => option.value);
}

export function normalizePushCountrySelection(nextValue = [], allCountryCodes = []) {
  const selected = (Array.isArray(nextValue) ? nextValue : []).filter(
    (value) => value && value !== "None"
  );
  const codes = Array.isArray(allCountryCodes) ? allCountryCodes : [];

  if (selected.includes(PUSH_SELECT_ALL_COUNTRIES)) {
    const withoutAll = selected.filter(
      (value) => value !== PUSH_SELECT_ALL_COUNTRIES
    );
    const allSelected =
      codes.length > 0 && codes.every((code) => withoutAll.includes(code));
    return allSelected ? [] : [...codes];
  }

  return selected.filter((value) => codes.includes(value));
}

export function cleanPushCtas(ctas = []) {
  const labelMax = notificationValidations.push.ctaLabel.maxLength;
  const targetMax = notificationValidations.push.ctaTarget.maxLength;
  return (Array.isArray(ctas) ? ctas : [])
    .filter((cta) => cta?.label?.trim() && cta?.target?.trim())
    .slice(0, PUSH_MAX_CTAS)
    .map((cta, index) => ({
      action: "Open",
      id: `button${index + 1}`,
      text: cta.label.trim().slice(0, labelMax),
      targetScreen: cta.target.trim().slice(0, targetMax),
    }));
}

/**
 * Push message.payload — Generic keys only (TL / force-upgrade contract):
 * description, imageUrl, targetScreen (deprecated: message, image, weblink).
 * Title & description are HTML fragments (no DOCTYPE / full document).
 */
export function buildPushMessagePayload(form = {}, expiryDateMs) {
  const description = sanitizeInAppNotificationHtml(form.description || "");
  const imageUrl = (form.image || "").trim();
  // Allow light HTML in title (e.g. <b>, emoji); never wrap as a full HTML document.
  const title = sanitizeInAppNotificationHtml(form.title || "").trim()
    || (form.title || "").trim();
  const targetScreen = (form.targetScreen || "").trim();

  const payload = {
    type: (form.type || "").trim(),
    category: (form.category || "").trim(),
    title,
    description,
    targetScreen,
    imageUrl,
  };

  const ts = getNotificationExpiryUnixSeconds(expiryDateMs);
  if (ts != null) {
    payload.ts = String(ts);
  }

  const priority = (form.priority || "").trim();
  if (priority) {
    payload.priority = priority;
  }

  const cleanedCtas = cleanPushCtas(form.ctas);
  if (cleanedCtas.length > 0) {
    payload.ctas = JSON.stringify(cleanedCtas);
  }

  return payload;
}

export function validatePushMessageForm(form = {}) {
  if (!(form.type || "").trim()) return "Please select Type";
  if (!(form.category || "").trim()) return "Please select Category";
  const allowedCategories = PUSH_TYPE_CATEGORY_MAP[form.type] || [];
  if (!allowedCategories.includes(form.category)) {
    return "Please select a valid Category for the selected Type";
  }
  if (!(form.title || "").trim()) return "Push notification title cannot be empty";

  const description = form.description || "";
  const hasText = Boolean(stripHtmlToPlainText(description));
  if (!hasText) {
    return "Push notification description cannot be empty";
  }

  if (!(form.targetScreen || "").trim()) return "Please enter Target Screen";
  if (!(form.image || "").trim()) return "Please upload an Image URL";

  const ctas = Array.isArray(form.ctas) ? form.ctas : [];
  if (ctas.length > PUSH_MAX_CTAS) {
    return `A maximum of ${PUSH_MAX_CTAS} CTAs is allowed`;
  }
  for (let i = 0; i < ctas.length; i += 1) {
    const label = ctas[i]?.label?.trim() || "";
    const target = ctas[i]?.target?.trim() || "";
    if ((label && !target) || (!label && target)) {
      return `CTA ${i + 1} requires both Text and Target Screen`;
    }
  }
  return null;
}

export function getOptionLabel(options = [], value, emptyLabel = "") {
  if (!value) return emptyLabel;
  return options.find((option) => option.value === value)?.label || value;
}
